<template>
    <section>
        <div class="entries">
            <div class="filters-container">
                <div class="date-filters">
                    <input 
                        type="date" 
                        class="form-control date-input" 
                        v-model="dateFrom"
                        placeholder="De">
                    <span class="date-separator">até</span>
                    <input 
                        type="date" 
                        class="form-control date-input" 
                        v-model="dateTo"
                        placeholder="Até">
                    <button class="btn btn-search" @click="applyDateFilter">
                        <i class="fas fa-search"></i> Buscar
                    </button>
                    <button class="btn btn-clear" @click="clearFilters" v-if="searchQuery || dateFrom || dateTo">
                        <i class="fas fa-times"></i> Limpar
                    </button>
                </div>
                <div class="info-filter">
                    <span>Situação do cadastro</span>
                    <select class="form-control" v-model="infoFilter">
                        <option value="">Todos</option>
                        <option value="full">Completo</option>
                        <option value="partial">Incompleto</option>
                    </select>
                </div>
            </div>
            
            <div class="filters-container">
                <div class="search-filter">
                    <input 
                    type="text" 
                    class="form-control search-input" 
                    v-model="searchQuery"
                    placeholder="Buscar um registro..."
                    @input="resetPagination">
                    <i class="fas fa-search search-icon"></i>
                </div>
                <div class="export-buttons-container">
                    <button class="btn btn-export btn-excel" @click="exportToExcel">
                        <i class="fas fa-file-excel"></i> Exportar Excel
                    </button>
                    <button class="btn btn-export btn-pdf" @click="generatePDF">
                        <i class="fas fa-file-pdf"></i> Exportar PDF
                    </button>
                </div>
            </div>
            

            <div class="entries-table-container">
                <table class="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th @click="sortBy('created_at')" class="sortable">
                                Data/Hora
                                <i :class="getSortIcon('created_at')"></i>
                            </th>
                            <th @click="sortBy('event')" class="sortable">
                                Evento
                                <i :class="getSortIcon('event')"></i>
                            </th>
                            <th @click="sortBy('protocol')" class="sortable">
                                Protocolo
                                <i :class="getSortIcon('protocol')"></i>
                            </th>
                            <th @click="sortBy('name')" class="sortable">
                                Nome
                                <i :class="getSortIcon('name')"></i>
                            </th>
                            <th @click="sortBy('phone')" class="sortable">
                                Telefone
                                <i :class="getSortIcon('phone')"></i>
                            </th>
                            <th @click="sortBy('email')" class="sortable">
                                Email
                                <i :class="getSortIcon('email')"></i>
                            </th>
                            <th @click="sortBy('full_info')" class="sortable">
                                Cadastro
                                <i :class="getSortIcon('full_info')"></i>
                            </th>
                            <th @click="sortBy('tries')" class="sortable">
                                Tentativas
                                <i :class="getSortIcon('tries')"></i>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="entry in paginatedEntries" :key="entry.id">
                            <td>{{ formatDate(entry.created_at) }}</td>
                            <td class="event-name">{{ entry.event }}</td>
                            <td class="protocol">{{ entry.protocol }}</td>
                            <td class="name">{{ entry.name || entry.client }}</td>
                            <td class="phone">{{ formatPhone(entry.phone) }}</td>
                            <td class="email">{{ entry.email || '-' }}</td>
                            <td>
                                <span :class="['badge', entry.full_info ? 'badge-success' : 'badge-danger']">
                                    {{ entry.full_info ? 'Completo' : 'Incompleto' }}
                                </span>
                            </td>
                            <td class="tries">{{ entry.tries }}</td>
                        </tr>
                        <tr v-if="filteredEntries.length === 0">
                            <td colspan="8" class="text-center">
                                Nenhum registro encontrado
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pagination-container" v-if="totalPages > 1">
                <div class="pagination-info">
                    Mostrando {{ startItem }} a {{ endItem }} de {{ filteredEntries.length }} registros
                </div>
                <div class="pagination-controls">
                    <button 
                        class="btn btn-pagination" 
                        @click="previousPage" 
                        :disabled="currentPage === 1">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="page-info">Página {{ currentPage }} de {{ totalPages }}</span>
                    <button 
                        class="btn btn-pagination" 
                        @click="nextPage" 
                        :disabled="currentPage === totalPages">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
                <div class="items-per-page">
                    <select v-model.number="itemsPerPage" @change="resetPagination" class="form-control">
                        <option :value="10">10 por página</option>
                        <option :value="25">25 por página</option>
                        <option :value="50">50 por página</option>
                        <option :value="100">100 por página</option>
                    </select>
                </div>
            </div>

            <!-- Vue HTML2PDF Component -->
            <vue-html2pdf
                :show-layout="false"
                :float-layout="true"
                :enable-download="true"
                :preview-modal="false"
                :paginate-elements-by-height="1400"
                :filename="`participacoes_${getCurrentDate()}`"
                :pdf-quality="2"
                :manual-pagination="false"
                pdf-format="a4"
                pdf-orientation="landscape"
                pdf-content-width="100%"
                ref="html2Pdf">
                <section slot="pdf-content">
                    <div class="pdf-content">
                        <h2>Relatório de Participações - {{ getCurrentDate() }}</h2>
                        <table class="pdf-table">
                            <thead>
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Evento</th>
                                    <th>Protocolo</th>
                                    <th>Nome</th>
                                    <th>Telefone</th>
                                    <th>Email</th>
                                    <th>Cadastro</th>
                                    <th>Tentativas</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="entry in filteredEntries" :key="entry.id">
                                    <td>{{ formatDate(entry.created_at) }}</td>
                                    <td>{{ entry.event }}</td>
                                    <td>{{ entry.protocol }}</td>
                                    <td>{{ entry.name || entry.client }}</td>
                                    <td>{{ formatPhone(entry.phone) }}</td>
                                    <td>{{ entry.email || '-' }}</td>
                                    <td>{{ entry.full_info ? 'Completo' : 'Incompleto' }}</td>
                                    <td>{{ entry.tries }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </vue-html2pdf>
        </div>
    </section>
</template>

<script>
    import VueHtml2pdf from 'vue-html2pdf'

    export default {
        name: 'EntriesList',
        components: {
            VueHtml2pdf
        },
        props: {
            eventsEntriesList: {
                type: Array,
                default: []
            }
        },
        data() {
            return {
                searchQuery: '',
                dateFrom: '',
                dateTo: '',
                appliedDateFrom: '',
                appliedDateTo: '',
                sortColumn: 'created_at',
                sortDirection: 'desc',
                currentPage: 1,
                itemsPerPage: 25,
                infoFilter: ''
            }
        },
        mounted() {
            // Data final: hoje
            this.dateTo = moment().format('YYYY-MM-DD');

            // Data inicial: primeiro dia do mês atual
            this.dateFrom = moment().startOf('month').format('YYYY-MM-DD');

            // Aplicar os filtros automaticamente
            this.applyDateFilter();
        },
        computed: {
            filteredEntries() {
                let filtered = [...this.eventsEntriesList];

                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase();
                    filtered = filtered.filter(entry => {
                        const name = (entry.name || entry.client || '').toLowerCase();
                        const email = (entry.email || '').toLowerCase();
                        const phone = (entry.phone || '').toLowerCase();
                        const protocol = (entry.protocol || '').toLowerCase();
                        const event = (entry.event || '').toLowerCase();

                        return name.includes(query) || 
                               email.includes(query) || 
                               phone.includes(query) || 
                               event.includes(query) || 
                               protocol.includes(query);
                    });
                }

                if (this.appliedDateFrom) {
                    const fromDate = moment(this.appliedDateFrom).startOf('day');
                    filtered = filtered.filter(entry => {
                        return moment(entry.created_at).isSameOrAfter(fromDate);
                    });
                }

                if (this.appliedDateTo) {
                    const toDate = moment(this.appliedDateTo).endOf('day');
                    filtered = filtered.filter(entry => {
                        return moment(entry.created_at).isSameOrBefore(toDate);
                    });
                }

                if(this.infoFilter) {
                    filtered = filtered.filter(el => {
                        if(this.infoFilter === 'full') return el.full_info === 1;
                        if(this.infoFilter === 'partial') return el.full_info === 0;
                    })
                }

                filtered.sort((a, b) => {
                    let aValue = a[this.sortColumn];
                    let bValue = b[this.sortColumn];

                    if (this.sortColumn === 'name') {
                        aValue = a.name || a.client || '';
                        bValue = b.name || b.client || '';
                    }

                    if (this.sortColumn === 'created_at' || this.sortColumn === 'updated_at') {
                        aValue = aValue ? moment(aValue).valueOf() : 0;
                        bValue = bValue ? moment(bValue).valueOf() : 0;
                    }

                    if (typeof aValue === 'string') {
                        aValue = aValue.toLowerCase();
                        bValue = (bValue || '').toLowerCase();
                    }

                    if (aValue === null || aValue === undefined) aValue = '';
                    if (bValue === null || bValue === undefined) bValue = '';

                    if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
                    if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
                    return 0;
                });

                return filtered;
            },

            totalPages() {
                return Math.ceil(this.filteredEntries.length / this.itemsPerPage);
            },

            paginatedEntries() {
                const start = (this.currentPage - 1) * this.itemsPerPage;
                const end = start + this.itemsPerPage;
                return this.filteredEntries.slice(start, end);
            },

            startItem() {
                return this.filteredEntries.length === 0 ? 0 : ((this.currentPage - 1) * this.itemsPerPage) + 1;
            },

            endItem() {
                const end = this.currentPage * this.itemsPerPage;
                return end > this.filteredEntries.length ? this.filteredEntries.length : end;
            }
        },
        methods: {
            formatDate(date) {
                if (!date) return '-';
                return moment(date).format('DD/MM/YYYY - HH:mm:ss');
            },

            formatPhone(phone) {
                if (!phone) return '-';
                const cleaned = phone.replace(/\D/g, '');

                if (cleaned.length === 13) {
                    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
                }
                if (cleaned.length === 11) {
                    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
                }
                return phone;
            },

            sortBy(column) {
                if (this.sortColumn === column) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
                } else {
                    this.sortColumn = column;
                    this.sortDirection = 'asc';
                }
            },

            getSortIcon(column) {
                if (this.sortColumn !== column) {
                    return 'fas fa-sort sort-icon';
                }
                return this.sortDirection === 'asc' 
                    ? 'fas fa-sort-up sort-icon active' 
                    : 'fas fa-sort-down sort-icon active';
            },

            applyDateFilter() {
                this.appliedDateFrom = this.dateFrom;
                this.appliedDateTo = this.dateTo;
                this.resetPagination();
            },

            clearFilters() {
                this.searchQuery = '';
                this.dateFrom = '';
                this.dateTo = '';
                this.appliedDateFrom = '';
                this.appliedDateTo = '';
                this.resetPagination();
            },

            resetPagination() {
                this.currentPage = 1;
            },

            previousPage() {
                if (this.currentPage > 1) {
                    this.currentPage--;
                }
            },

            nextPage() {
                if (this.currentPage < this.totalPages) {
                    this.currentPage++;
                }
            },

            getCurrentDate() {
                return moment().format('DD-MM-YYYY');
            },

            exportToExcel() {
                const data = this.filteredEntries.map(entry => [
                    this.formatDate(entry.created_at),
                    entry.event,
                    entry.protocol,
                    entry.name || entry.client,
                    this.formatPhone(entry.phone),
                    entry.email || '-',
                    entry.full_info ? 'Completo' : 'Incompleto',
                    entry.tries
                ]);

                const headers = ['Data/Hora', 'Evento', 'Protocolo', 'Nome', 'Telefone', 'Email', 'Cadastro', 'Tentativas'];
                const csvContent = [
                    headers.join(';'),
                    ...data.map(row => row.map(cell => `"${cell}"`).join(';'))
                ].join('\n');

                const BOM = '\uFEFF';
                const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `entradas_${this.getCurrentDate()}.csv`;
                link.click();
            },

            generatePDF() {
                this.$refs.html2Pdf.generatePdf();
            }
        }
    }
</script>

<style lang="scss" scoped>
    .entries {
        padding: 20px;
        max-width: 1600px;
        margin: 0 auto;

        .filters-container {
            display: flex;
            gap: 20px;
            margin-bottom: 15px;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;

            .search-filter {
                flex: 1;
                min-width: 300px;
                position: relative;

                .search-input {
                    padding-left: 40px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    height: 40px;

                    &:focus {
                        border-color: #1c869a;
                        outline: none;
                        box-shadow: 0 0 0 3px rgba(28, 134, 154, 0.1);
                    }
                }

                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #6c757d;
                }
            }

            .date-filters {
                display: flex;
                gap: 10px;
                align-items: center;

                .date-input {
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    height: 40px;
                    padding: 0 10px;

                    &:focus {
                        border-color: #1c869a;
                        outline: none;
                        box-shadow: 0 0 0 3px rgba(28, 134, 154, 0.1);
                    }
                }

                .date-separator {
                    color: #6c757d;
                    font-size: 14px;
                }

                .btn-search {
                    background-color: #1c869a;
                    color: white;
                    border: none;
                    border-radius: 4px !important;
                    padding: 8px 15px;
                    font-size: 12px;
                    font-weight: 600;
                    transition: all 0.3s;
                    cursor: pointer;
                    white-space: nowrap;

                    &:hover {
                        background-color: #196d7e;
                        transform: translateY(-2px);
                    }

                    i {
                        margin-right: 5px;
                    }
                }

                .btn-clear {
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    border-radius: 4px !important;
                    padding: 8px 15px;
                    font-size: 12px;
                    font-weight: 600;
                    transition: all 0.3s;
                    cursor: pointer;
                    white-space: nowrap;

                    &:hover {
                        background-color: #5a6268;
                        transform: translateY(-2px);
                    }

                    i {
                        margin-right: 5px;
                    }
                }
            }

            info-filters {
                display: flex;
                white-space: nowrap;
                gap: 5px;
                font-weight: 600;
                align-items: center;
            }
        }

        .export-buttons-container {
            display: flex;
            gap: 10px;

            .btn-export {
                padding: 10px 20px;
                border: none;
                border-radius: 4px !important;
                font-size: 13px;
                font-weight: 600;
                color: white;
                transition: all 0.3s;
                cursor: pointer;

                i {
                    margin-right: 8px;
                }

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
                }

                &.btn-excel {
                    background-color: #217346;

                    &:hover {
                        background-color: #1a5c37;
                    }
                }

                &.btn-pdf {
                    background-color: #d32f2f;

                    &:hover {
                        background-color: #b71c1c;
                    }
                }
            }
        }

        .entries-table-container {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            overflow: auto;
            max-height: 50vh;

            .table {
                margin-bottom: 0;
                text-align: center;
                font-size: 13px;

                thead {
                    background: linear-gradient(135deg, #1c869a 0%, #187283 100%);
                    position: sticky;
                    top: 0;
                    z-index: 10;

                    tr th {
                        color: white;
                        font-weight: 600;
                        text-transform: uppercase;
                        font-size: 11px;
                        letter-spacing: 0.5px;
                        border: none;
                        padding: 12px 10px;

                        &.sortable {
                            cursor: pointer;
                            user-select: none;
                            transition: all 0.2s;

                            &:hover {
                                background-color: rgba(255, 255, 255, 0.1);
                            }

                            .sort-icon {
                                margin-left: 5px;
                                font-size: 10px;
                                opacity: 0.5;
                                transition: opacity 0.2s;

                                &.active {
                                    opacity: 1;
                                }
                            }
                        }
                    }
                }

                tbody {
                    tr {
                        transition: all 0.2s;

                        &:hover {
                            background-color: #f1f3f5;
                        }

                        td {
                            padding: 10px;
                            vertical-align: middle;
                            border-top: 1px solid #dee2e6;
                            font-size: 14px;

                            &.event-name {
                                font-weight: 600;
                                color: #1c869a;
                            }

                            &.protocol {
                                color: #6c757d;
                            }

                            &.name {
                                font-weight: 500;
                            }

                            &.email {
                                color: #6c757d;
                                font-size: 14px;
                            }

                            &.tries {
                                font-weight: 600;
                            }

                            .badge {
                                padding: 4px 10px;
                                font-size: 10px;
                                font-weight: 600;
                                border-radius: 12px;
                            }

                            &.text-muted {
                                padding: 40px 15px;
                                font-style: italic;
                            }
                        }
                    }
                }
            }
        }

        .pagination-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
            padding: 15px 20px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

            .pagination-info {
                font-size: 13px;
                color: #6c757d;
            }

            .pagination-controls {
                display: flex;
                align-items: center;
                gap: 15px;

                .btn-pagination {
                    background-color: #1c869a;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    padding: 8px 12px;
                    cursor: pointer;
                    transition: all 0.3s;

                    &:hover:not(:disabled) {
                        background-color: #196d7e;
                        transform: translateY(-2px);
                    }

                    &:disabled {
                        background-color: #ccc;
                        cursor: not-allowed;
                        opacity: 0.6;
                    }
                }

                .page-info {
                    font-size: 14px;
                    font-weight: 500;
                    color: #495057;
                }
            }

            .items-per-page {
                select {
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    padding: 6px 10px;
                    font-size: 13px;

                    &:focus {
                        border-color: #1c869a;
                        outline: none;
                    }
                }
            }
        }
    }

    // Estilos para o PDF
    .pdf-content {
        padding: 20px;

        h2 {
            text-align: center;
            color: #1c869a;
            margin-bottom: 20px;
        }

        .pdf-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;

            thead {
                background-color: #1c869a;
                color: white;

                th {
                    padding: 8px 5px;
                    text-align: center;
                    border: 1px solid #187283;
                }
            }

            tbody {
                tr {
                    &:nth-child(even) {
                        background-color: #f8f9fa;
                    }

                    td {
                        padding: 6px 5px;
                        text-align: center;
                        border: 1px solid #dee2e6;
                    }
                }
            }
        }
    }

    @media (max-width: 768px) {
        .entries {
            padding: 10px;

            .filters-container {
                flex-direction: column;

                .search-filter,
                .date-filters {
                    width: 100%;
                }
            }

            .export-buttons-container {
                flex-direction: column;

                .btn-export {
                    width: 100%;
                }
            }

            .pagination-container {
                flex-direction: column;
                gap: 15px;
            }
        }
    }
</style>